import { FC, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  CMS_CHANGE_FREQUENCIES,
  CMS_LOCALE_NAMES,
  CMS_LOCALES,
  CMS_SITEMAP_PRIORITIES,
  CmsChangeFrequency,
  CmsPage,
  CmsSitemapInclude,
  SEO_DESCRIPTION_LIMIT,
  SEO_TITLE_LIMIT
} from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { Counter, Field, FieldRow, inputClass, PathText, RailRow, SectionHeading, ToggleRow } from "../CmsUi"

import { SerpPreview } from "./SerpPreview"

interface SeoPaneProps {
  page: CmsPage
  onChange: (patch: Partial<CmsPage>) => void
}

export const SeoPane: FC<SeoPaneProps> = ({ page, onChange }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop")
  const site = useBrandSite()

  return (
    <div>
      <SectionHeading>{t("cmsSeo.serpHeading", { defaultValue: "Search result preview" })}</SectionHeading>

      <SerpPreview
        host={site.host}
        path={site.path(page.locale, page.path)}
        title={page.seo_title}
        description={page.seo_description}
        device={device}
      />

      <p className="mt-2 mb-6 text-[11.5px] text-gray-400 dark:text-gray-500">
        {t("cmsSeo.serpHint", { defaultValue: "Preview uses Google's pixel limits." })}{" "}
        <button
          type="button"
          className="text-purple-500 underline decoration-dotted underline-offset-2"
          onClick={() => setDevice(device === "desktop" ? "mobile" : "desktop")}
        >
          {device === "desktop"
            ? t("cmsSeo.switchMobile", { defaultValue: "Switch to mobile" })
            : t("cmsSeo.switchDesktop", { defaultValue: "Switch to desktop" })}
        </button>
      </p>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.titleTag", { defaultValue: "Title tag" })}
          counter={<Counter length={page.seo_title.length} limit={SEO_TITLE_LIMIT} />}
        >
          <input className={inputClass} value={page.seo_title} onChange={(event) => onChange({ seo_title: event.target.value })} />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.metaDescription", { defaultValue: "Meta description" })}
          counter={<Counter length={page.seo_description.length} limit={SEO_DESCRIPTION_LIMIT} />}
        >
          <textarea
            className={inputClass}
            rows={3}
            value={page.seo_description}
            onChange={(event) => onChange({ seo_description: event.target.value })}
          />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.canonical", { defaultValue: "Canonical URL" })}
          hint={t("cmsSeo.field.canonicalHint", {
            defaultValue: "Leave empty and the page canonicalises to itself. Only set this when the page genuinely duplicates another URL."
          })}
        >
          <input
            className={inputClass}
            value={page.canonical ?? ""}
            onChange={(event) => onChange({ canonical: event.target.value.trim() || null })}
            placeholder={site.url(page.locale, page.path)}
          />
        </Field>
      </FieldRow>

      <SectionHeading>{t("cmsSeo.indexingHeading", { defaultValue: "Indexing" })}</SectionHeading>

      <ToggleRow
        title={t("cmsSeo.field.index", { defaultValue: "Allow search engines to index" })}
        description={t("cmsSeo.field.indexHint", { defaultValue: "Off emits noindex and drops the URL from the sitemap" })}
        checked={page.robots_index}
        onChange={() => onChange({ robots_index: !page.robots_index })}
      />
      <ToggleRow
        title={t("cmsSeo.field.follow", { defaultValue: "Allow following links" })}
        description={t("cmsSeo.field.followHint", { defaultValue: "Off emits nofollow" })}
        checked={page.robots_follow}
        onChange={() => onChange({ robots_follow: !page.robots_follow })}
      />

      <FieldRow className="mt-4">
        <Field label={t("cmsSeo.field.robotsAdvanced", { defaultValue: "Advanced robots directives" })}>
          <input
            className={inputClass}
            value={page.robots_advanced ?? ""}
            onChange={(event) => onChange({ robots_advanced: event.target.value.trim() || null })}
            placeholder="max-image-preview:large, max-snippet:-1"
          />
        </Field>
      </FieldRow>

      <SectionHeading>{t("cmsSeo.sitemapHeading", { defaultValue: "Sitemap" })}</SectionHeading>

      <FieldRow>
        <Field label={t("cmsSeo.field.sitemapInclude", { defaultValue: "Include in sitemap" })}>
          <select
            className={inputClass}
            value={page.sitemap_include}
            onChange={(event) => onChange({ sitemap_include: event.target.value as CmsSitemapInclude })}
          >
            <option value="auto">{t("cmsSeo.sitemapAuto", { defaultValue: "Yes — follows indexing" })}</option>
            <option value="yes">{t("cmsSeo.sitemapYes", { defaultValue: "Always" })}</option>
            <option value="no">{t("cmsSeo.sitemapNo", { defaultValue: "No" })}</option>
          </select>
        </Field>
        <Field label={t("cmsSeo.field.priority", { defaultValue: "Priority" })}>
          <select
            className={inputClass}
            value={page.sitemap_priority.toFixed(1)}
            onChange={(event) => onChange({ sitemap_priority: Number(event.target.value) })}
          >
            {CMS_SITEMAP_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("cmsSeo.field.changefreq", { defaultValue: "Change frequency" })}>
          <select
            className={inputClass}
            value={page.sitemap_changefreq}
            onChange={(event) => onChange({ sitemap_changefreq: event.target.value as CmsChangeFrequency })}
          >
            {CMS_CHANGE_FREQUENCIES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
      </FieldRow>

      <SectionHeading>{t("cmsSeo.hreflangHeading", { defaultValue: "Language versions (hreflang)" })}</SectionHeading>

      {CMS_LOCALES.map((locale) => {
        const translated = page.translations[locale]
        return (
          <RailRow
            key={locale}
            label={`${CMS_LOCALE_NAMES[locale]} (${locale.toUpperCase()})`}
            value={
              translated ? (
                <PathText>{translated.path}</PathText>
              ) : (
                <span className="text-xs text-rose-500">
                  {t("cmsSeo.notTranslated", { defaultValue: "Not translated — no hreflang emitted" })}
                </span>
              )
            }
          />
        )
      })}
    </div>
  )
}
